import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ShoppingBag,
  Compass,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Layers,
  Award,
  MessageCircle,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { updateSEO } from "../utils/seo";
import "./NotFound.css";

// Official WhatsApp icon SVG for concierge recovery
function WhatsAppIcon({ size = 18, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export default function NotFound() {
  const { t, isRTL, language } = useLanguage();

  useEffect(() => {
    updateSEO({
      title: t("notFound.metaTitle"),
      description: t("notFound.desc"),
    });
  }, [t, language]);

  const whatsappUrl = "https://wa.me/201099921862";

  const quickLinks = [
    {
      to: "/collections/bathrobes",
      icon: Sparkles,
      titleKey: "notFound.linkBathrobes",
      descKey: "notFound.linkBathrobesDesc",
    },
    {
      to: "/collections/towels",
      icon: Layers,
      titleKey: "notFound.linkTowels",
      descKey: "notFound.linkTowelsDesc",
    },
    {
      to: "/about",
      icon: Award,
      titleKey: "notFound.linkAbout",
      descKey: "notFound.linkAboutDesc",
    },
    {
      to: "/contact",
      icon: MessageCircle,
      titleKey: "notFound.linkContact",
      descKey: "notFound.linkContactDesc",
    },
  ];

  return (
    <div className="not-found-page" dir={isRTL ? "rtl" : "ltr"}>
      <div className="not-found-container">
        {/* ── Main Hero Card ── */}
        <section className="not-found-card" aria-labelledby="not-found-heading">
          {/* Decorative 404 Watermark */}
          <div className="not-found-watermark" aria-hidden="true">
            404
          </div>

          {/* Luxury Eyebrow Pill */}
          <div className="not-found-badge">
            <Compass size={13} className="not-found-badge__icon" />
            <span>{t("notFound.eyebrow")}</span>
          </div>

          {/* Display 404 Code */}
          <div className="not-found-code" aria-hidden="true">
            <span className="not-found-code__digit">4</span>
            <span className="not-found-code__digit">0</span>
            <span className="not-found-code__digit">4</span>
          </div>

          {/* Heading & Explanation */}
          <h1 id="not-found-heading" className="not-found-title">
            {t("notFound.title")}
          </h1>
          <p className="not-found-desc">{t("notFound.desc")}</p>

          {/* Primary Action Buttons */}
          <div className="not-found-actions">
            <Link to="/" className="not-found-btn not-found-btn--primary">
              <Home size={18} />
              <span>{t("notFound.backHome")}</span>
              {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
            </Link>

            <Link to="/shop" className="not-found-btn not-found-btn--secondary">
              <ShoppingBag size={18} />
              <span>{t("notFound.exploreShop")}</span>
            </Link>
          </div>
        </section>

        {/* ── Popular Destinations Grid ── */}
        <section
          className="not-found-destinations"
          aria-label={t("notFound.quickLinksTitle")}
        >
          <h2 className="not-found-destinations__title">
            {t("notFound.quickLinksTitle")}
          </h2>

          <div className="not-found-grid">
            {quickLinks.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={idx}
                  to={item.to}
                  className="not-found-grid-card"
                >
                  <div className="not-found-grid-card__icon-box">
                    <IconComponent size={20} />
                  </div>
                  <div className="not-found-grid-card__content">
                    <h3 className="not-found-grid-card__title">
                      {t(item.titleKey)}
                    </h3>
                    <p className="not-found-grid-card__desc">
                      {t(item.descKey)}
                    </p>
                  </div>
                  <span
                    className="not-found-grid-card__arrow"
                    aria-hidden="true"
                  >
                    {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ── Concierge Support Bar ── */}
        <div className="not-found-concierge">
          <div className="not-found-concierge__text">
            <span className="not-found-concierge__brand">TERRY LAND CONCIERGE</span>
            <p className="not-found-concierge__prompt">
              {t("notFound.helpNote")}
            </p>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="not-found-concierge__btn"
          >
            <WhatsAppIcon size={18} />
            <span>{t("notFound.whatsappChat")}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
